import { convertFromDays } from '@/utils/conversions';

export const baseLocale = (locale: string): string =>
    locale.trim().toLowerCase().split(/[-_]/)[0];

export const normalizeForSubmit = (value: string): string => {
    return value
        .replace(/\u00A0/g, ' ')
        .replace(/\u200B/g, '')
        .trim();
};

export const parseNumericWithUnit = (
    value: unknown,
): { numberValue: string; unit: string } | null => {
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();

    // Supports:
    // 37.5 °C
    // 98 %
    // 110/70 mmHg
    // -1.2 mmol/L
    const match = /^(-?\d+(?:\.\d+)?(?:\/-?\d+(?:\.\d+)?)?)\s+(.+)$/.exec(trimmed);

    if (!match) return null;

    return {
        numberValue: match[1],
        unit: match[2].trim(),
    };
};

export const formatLabel = (
    value?: string | null,
) => {
    if (!value) return '';

    return value
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());
};

type Translate = (token: string) => string;
type FormatNumber = (value: number) => string;

const formatTimeValue = (
    value: number,
    singularToken: string,
    pluralToken: string,
    translate: Translate,
    formatNumber: FormatNumber,
): string => {
    const unitToken = value === 1 ? singularToken : pluralToken;
    return `${formatNumber(value)} ${translate(unitToken)}`;
};

const toRoundedConvertedDays = (
    unit: 'months' | 'years',
    ageInDays: number,
): number => {
    return Number(convertFromDays(unit, 0, ageInDays));
};

const formatMonthsAndYears = (
    months: number,
    translate: Translate,
    formatNumber: FormatNumber,
): string => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    const monthsPart = remainingMonths === 0 ? null : formatTimeValue(remainingMonths, 'partials.time.month', 'partials.time.months', translate, formatNumber);

    return [
        formatTimeValue(years, 'partials.time.year', 'partials.time.years', translate, formatNumber),
        monthsPart
    ].filter(part => part != null).join(' ');
};

export const formatAgeInDays = (
    value: unknown,
    translate: Translate,
    formatNumber: FormatNumber,
): string => {
    const ageInDays = typeof value === 'number' ? value : Number.NaN;

    if (!Number.isFinite(ageInDays) || ageInDays < 0) {
        return '';
    }

    if (ageInDays < 61) {
        const days = Math.round(ageInDays);
        return formatTimeValue(days, 'partials.time.day', 'partials.time.days', translate, formatNumber);
    }

    if (ageInDays < 365) {
        const months = toRoundedConvertedDays('months', ageInDays);
        if (!Number.isFinite(months)) return '';

        return formatTimeValue(months, 'partials.time.month', 'partials.time.months', translate, formatNumber);
    }

    if (ageInDays < 1825) {
        const months = toRoundedConvertedDays('months', ageInDays);
        if (!Number.isFinite(months)) return '';

        return `${formatTimeValue(months, 'partials.time.month', 'partials.time.months', translate, formatNumber)} (${formatMonthsAndYears(months, translate, formatNumber)})`;
    }

    const years = toRoundedConvertedDays('years', ageInDays);
    if (!Number.isFinite(years)) return '';

    return formatTimeValue(years, 'partials.time.year', 'partials.time.years', translate, formatNumber);
};
